<template>
  <v-container fluid>
    <v-card :loading="loading">
      <v-card-title>
        <h2>
          <v-btn
            depressed
            class="mx-2"
            fab
            small
            :to="{ name: 'cliente-list' }"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="loading">Carregando...</span>
          <span v-else>
            {{
              geral.id !== ""
                ? `Alterar cliente: ${geral.id} - ${geral.nome}`
                : "Novo cliente"
            }}
          </span>
        </h2>
      </v-card-title>
      <form @submit.prevent="submitForm">
        <v-tabs v-model="tab" grow>
          <v-tab>
            <v-icon
              left
              color="error"
              v-if="$v.geral.$anyDirty && $v.geral.$invalid"
              class="mdi mdi-alert-circle"
            ></v-icon>
            <span>Geral</span>
          </v-tab>
          <v-tab :disabled="loading">
            <v-icon v-if="semEmail" left color="error">
              mdi-alert-circle
            </v-icon>
            Contatos
          </v-tab>
          <v-tab :disabled="loading">
            <v-icon v-if="semEndereco" left color="error">
              mdi-alert-circle
            </v-icon>
            Endereços
          </v-tab>
          <v-tab :disabled="loading">Documentos</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card-text>
              <v-radio-group v-model="geral.tipo" row>
                <v-radio label="Pessoa júridica" value="J" />
                <v-radio label="Pessoa física" value="F" />
              </v-radio-group>
              <v-text-field
                v-model="geral.cpf_cnpj"
                v-touch="geral.cpf_cnpj"
                v-mask="geral.tipo === 'J' ? 'cnpj' : 'cpf'"
                :error-messages="cpfCnpjErrors"
                :maxlength="docMaxlength"
                :label="docLabel"
                :append-outer-icon="
                  geral.tipo === 'J' ? 'mdi-cloud-search-outline' : ''
                "
                @blur="buscarCNPJ()"
                @click:append-outer="buscarCNPJ()"
                :loading="loadingCNPJ"
                :disabled="loadingCNPJ || geral.id !== ''"
              />
              <v-text-field
                v-model="geral.nome"
                v-touch="geral.nome"
                :error-messages="nomeErrors"
                :counter="80"
                :label="nomeLabel"
              ></v-text-field>
              <v-text-field
                v-model="geral.razao_social"
                v-touch="geral.razao_social"
                :error-messages="razaoSocialErrors"
                :counter="80"
                label="Razão Social"
              ></v-text-field>
              <v-text-field
                v-if="geral.tipo === 'J'"
                v-model="geral.inscricao_estadual"
                v-touch="geral.inscricao_estadual"
                :error-messages="inscricaoEstadualErrors"
                :counter="20"
                label="Inscrição Estadual"
              ></v-text-field>
              <v-text-field
                v-if="geral.tipo === 'J'"
                v-model="geral.inscricao_municipal"
                v-touch="geral.inscricao_municipal"
                :error-messages="inscricaoMunicipalErrors"
                :counter="20"
                label="Inscrição Municipal"
              ></v-text-field>
              <v-autocomplete
                v-model="geral.id_vendedor"
                :items="vendedores"
                :search-input.sync="pesquisa"
                v-touch="geral.id_vendedor"
                :error-messages="vendedorErrors"
                no-filter
                :loading="pesquisando"
                item-text="nome"
                item-value="id"
                label="Vendedor responsável"
                clearable
                no-data-text="Nenhum vendedor encontrado"
                placeholder="Pesquise por nome ou número da matrícula (funcionários)"
                prepend-icon="mdi-database-search"
              />
              <v-textarea
                auto-grow
                v-model="geral.descricao"
                label="Observações"
                hint="Observações pertinentes a respeito desde cliente"
                persistent-hint
              ></v-textarea>
              <v-switch v-model="geral.ativo" label="Ativo" />
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <div class="pa-2">
              <v-btn
                block
                text
                color="primary"
                @click="$store.commit('contato/novoContato')"
              >
                <v-icon class="mr-2">mdi-account-plus</v-icon>NOVO CONTATO
              </v-btn>
            </div>
            <contato-lista
              v-model="contatos"
              editando
              @select="$store.commit('contato/setContatoAtivo', $event)"
            />
            <v-alert type="error" class="mt-5 mx-5" v-if="semEmail" outlined>
              O cliente deve ter ao menos um contato com pelo menos um email e
              um telefone cadastrado.
            </v-alert>
          </v-tab-item>
          <v-tab-item>
            <lista-endereco v-model="enderecos" alterar />
            <v-alert type="error" class="mt-5 mx-5" v-if="semEndereco" outlined>
              O cliente deve ter ao menos um endereço cadastrado.
            </v-alert>
          </v-tab-item>
          <v-tab-item>
            <lista-documentos
              urlBase="clientes"
              :id-proprietario="geral.id"
              v-model="documentos"
              alterar
            />
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-btn depressed color="primary" @click="submit" :loading="loading"
            >Salvar</v-btn
          >
        </v-card-actions>
      </form>
    </v-card>
    <contato-edita-dialog @confirm="confirmarContato" />
    <conflito-clientes
      :existentes="clientesExistentes"
      @voltar="$router.replace({ name: 'cliente-list' })"
      @continuar="clientesExistentes = []"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { validarCPF, validarCNPJ, buscarCNPJ } from "../lib/receitaws";
import ListaDocumentos from "../components/documentos/ListaDocumentos";
import ListaEndereco from "../components/entidades/ListaEndereco";
import ContatoLista from "../components/contatos/ContatoLista";
import ContatoEditaDialog from "../components/contatos/ContatoEditaDialog";
import ContatoMixin from "../components/contatos/ContatoMixin";
import ConflitoClientes from "../components/clientes/ConflitoClientes";

export default {
  components: {
    ListaDocumentos,
    ListaEndereco,
    ContatoLista,
    ContatoEditaDialog,
    ConflitoClientes,
  },
  mixins: [validationMixin, ContatoMixin],

  validations() {
    const regras = {
      geral: {
        nome: { required, maxLength: maxLength(80) },
        razao_social: { maxLength: maxLength(80), required },
        cpf_cnpj: { required },
        inscricao_estadual: { maxLength: maxLength(20) },
        inscricao_municipal: { maxLength: maxLength(20) },
        id_vendedor: { required },
      },
    };
    if (this.geral.tipo === "J") {
      regras.geral.cpf_cnpj.cnpj = validarCNPJ;
    } else {
      regras.geral.cpf_cnpj.cpf = validarCPF;
    }

    // if (this.geral.id === "") {
    //   regras.geral.cpf_cnpj.existe = value =>
    //     this.$store.dispatch("entidade/checar", {
    //       tipo: "clientes",
    //       chave: value
    //     });
    // }
    return regras;
  },

  data() {
    return {
      geral: {
        id: "",
        id_vendedor: null,
        nome: "",
        razao_social: "",
        tipo: "J",
        cpf_cnpj: "",
        ativo: true,
        inscricao_estadual: "",
        inscricao_municipal: "",
        descricao: "",
      },
      enderecos: [],
      documentos: [],
      tab: null,
      loading: false,
      pesquisando: false,
      pesquisa: null,
      loadingCNPJ: false,
      contatoAtivo: null,
      clientesExistentes: [],
    };
  },

  computed: {
    nomeLabel() {
      return "Nome fantasia";
    },
    docLabel() {
      return this.geral.tipo === "F" ? "CPF" : "CNPJ";
    },
    docMaxlength() {
      return this.geral.tipo === "F" ? 14 : 18;
    },
    nomeErrors() {
      const errors = [];
      if (!this.$v.geral.nome.$dirty) return errors;
      !this.$v.geral.nome.required &&
        errors.push(`${this.nomeLabel} é obrigatório`);
      !this.$v.geral.nome.maxLength && errors.push("Somente 80 caracteres");
      return errors;
    },
    razaoSocialErrors() {
      const errors = [];
      if (!this.$v.geral.razao_social.$dirty) return errors;
      !this.$v.geral.razao_social.required &&
        errors.push("Razão social é obrigatório");
      !this.$v.geral.razao_social.maxLength &&
        errors.push("Somente 80 caracteres");
      return errors;
    },
    cpfCnpjErrors() {
      const errors = [];
      if (!this.$v.geral.cpf_cnpj.$dirty) return errors;
      !this.$v.geral.cpf_cnpj.required &&
        errors.push(`${this.docLabel} é obrigatório`);
      this.geral.tipo === "J" &&
        !this.$v.geral.cpf_cnpj.cnpj &&
        errors.push("CNPJ inválido");
      this.geral.tipo !== "J" &&
        !this.$v.geral.cpf_cnpj.cpf &&
        errors.push("CPF inválido");
      // this.geral.id === "" &&
      //   !this.$v.geral.cpf_cnpj.existe &&
      //   errors.push("Já existe um cliente cadastrado com este documento");
      return errors;
    },
    inscricaoEstadualErrors() {
      const errors = [];
      if (!this.$v.geral.inscricao_estadual.$dirty) return errors;
      !this.$v.geral.inscricao_estadual.maxLength &&
        errors.push("Somente 20 caracteres");
      return errors;
    },
    inscricaoMunicipalErrors() {
      const errors = [];
      if (!this.$v.geral.inscricao_municipal.$dirty) return errors;
      !this.$v.geral.inscricao_municipal.maxLength &&
        errors.push("Somente 20 caracteres");
      return errors;
    },
    vendedorErrors() {
      const errors = [];
      if (!this.$v.geral.id_vendedor.$dirty) return errors;
      !this.$v.geral.id_vendedor.required &&
        errors.push("Informe o vendedor responsável");

      return errors;
    },
    semEndereco() {
      return (
        this.enderecos.filter((endereco) => !(endereco.remover || false))
          .length == 0
      );
    },
    semEmail() {
      return (
        this.contatos.filter(
          (contato) =>
            !(contato.remover || false) &&
            contato.emails.length > 0 &&
            contato.telefones.length > 0
        ).length == 0
      );
    },
    ...mapState({
      vendedores: (state) => state.vendedor.vendedores,
    }),
  },

  methods: {
    async buscarCNPJ() {
      if (this.$v.geral.cpf_cnpj.$invalid || this.loadingCNPJ) {
        return;
      }
      try {
        this.loadingCNPJ = true;
        const existentes = await this.$store.dispatch("cliente/listar", {
          carregarVendedor: true,
          filtros: {
            cpf_cnpj: this.geral.cpf_cnpj,
          },
        });
        if (existentes.data.total > 0) {
          this.clientesExistentes = existentes.data.clientes;
        }
        if (this.geral.tipo !== "J") {
          this.loadingCNPJ = false;
          return;
        }

        const dados = await buscarCNPJ(this.geral.cpf_cnpj);
        this.geral.razao_social = dados.nome;
        this.geral.nome = dados.fantasia;

        if (dados.email !== "" || dados.telefones.length > 0) {
          const contato = {
            id: new Date().getTime(),
            nome: "Receita federal",
            observacoes: "Informações obtidas via consulta automática",
            tipos: ["receita"],
            telefones: [],
            emails: [],
            novo: true,
          };
          if (dados.email !== "") {
            contato.emails.push({
              id: new Date().getTime(),
              nome: "Receita Federal",
              descricao: "",
              email: dados.email,
              principal: false,
              novo: true,
              valido: true,
            });

            for (let i = 1; i <= dados.telefones.length; i++) {
              contato.telefones.push({
                id: new Date().getTime(),
                nome: `Receita Federal ${i}`,
                descricao: "",
                numero: "0" + dados.telefones[i - 1],
                celular: false,
                whatsapp: false,
                telegram: false,
                principal: false,
                novo: true,
                valido: true,
              });
            }
          }
          this.contatos.push(contato);
        }

        if (dados.cep !== "") {
          try {
            const cep = await this.$store.dispatch(
              "endereco/carregarCEP",
              dados.cep
            );

            const endereco = {
              nome: "Receita Federal",
              descricao: "Informações obtidas via consulta automática",
              principal: false,
              cep: dados.cep,
              endereco:
                cep.logradouro !== "" ? cep.logradouro : dados.logradouro,
              bairro: cep.bairro !== "" ? cep.bairro : dados.bairro,
              numero: dados.numero,
              complemento:
                cep.complemento !== "" ? cep.complemento : dados.complemento,
              cidade: cep.cidade,
              id_cidade: cep.cidade.id,
              novo: true,
              tipos: ["receita", "cobranca"],
              valido: true,
            };

            this.enderecos.push(endereco);
          } catch (err) {
            console.log(err);
          }
        }
      } catch (err) {
        this.$store.commit("ui/setSnackbar", { text: err });
      }
      this.loadingCNPJ = false;
    },
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.semEndereco && !this.semEmail) {
        this.loading = true;
        try {
          const cliente = await this.$store.dispatch(
            "cliente/gravar",
            this.geral
          );
          const id_entidade = cliente.id;
          const promises = [];
          if (this.documentos.length > 0) {
            promises.push(
              this.$store.dispatch("documento/gravar", {
                urlBase: "clientes",
                IDProprietario: cliente.id,
                documentos: this.documentos,
              })
            );
          }
          if (this.contatos.length > 0) {
            promises.push(
              this.$store.dispatch("contato/gravarContatos", {
                tipo_entidade: "clientes",
                id_entidade,
                contatos: this.contatos,
              })
            );
          }
          if (this.enderecos.length > 0) {
            promises.push(
              this.$store.dispatch("entidade/gravarEnderecos", {
                tipo: "clientes",
                id_entidade,
                enderecos: this.enderecos,
              })
            );
          }
          if (promises.length > 0) {
            await Promise.all(promises);
          }
          this.$router.replace({ path: "lista", name: "cliente-list" });
        } catch (err) {
          console.log(err);
          this.$store.commit("ui/setSnackbar", {
            text: "Não foi possível salvar o cliente",
          });
        } finally {
          this.loading = false;
        }
      }
    },
    pesquisarVendedores: _.debounce(async function() {
      this.pesquisando = true;
      await this.$store.dispatch("vendedor/listar", {
        pesquisa: this.pesquisa,
      });
      this.pesquisando = false;
    }, 300),
  },
  async mounted() {
    this.$store.commit("ui/changeToolbar", {
      title: "Cadastro de clientes",
    });
    if (this.$route.params.id) {
      this.geral.id = this.$route.params.id;

      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "cliente/carregar",
          this.geral.id
        );
        this.geral = {
          id: response.data.id,
          id_vendedor: response.data.id_vendedor,
          ativo: response.data.ativo,
          cpf_cnpj: response.data.cpf_cnpj,
          inscricao_estadual: response.data.inscricao_estadual,
          inscricao_municipal: response.data.inscricao_municipal,
          nome: response.data.nome,
          razao_social: response.data.razao_social,
          tipo: response.data.tipo,
          descricao: response.data.descricao,
        };
        this.enderecos = response.data.enderecos;
        this.contatos = response.data.contatos;

        if (response.data.id_vendedor) {
          await this.$store.commit("vendedor/carregar", [
            response.data.vendedor,
          ]);
        }
      } catch (err) {
        console.log(err);
        this.$store.commit("ui/setSnackbar", {
          text: "Não foi possível carregar o cliente",
        });
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    pesquisa() {
      this.pesquisarVendedores();
    },
  },
};
</script>
